import {
    endOfMonth,
    endOfWeek,
    getUnixTime,
    startOfMonth,
    startOfWeek,
} from 'date-fns'
import { IOption, TAcceptedInputValues } from '../../components/Form/types'
import {
    IFormPlace,
    IPlace,
    IServiceProps,
} from '../../components/Services/types'
import { wbkFormat } from '../../../admin/components/Form/utils/dateTime'
import { IBookingFormContext, IBookingFormObj } from './types'

export const defautlFields = [
    'first_name',
    'last_name',
    'email',
    'phone',
    'description',
]

export const getSelectedServicesValue = (services: IServiceProps[]) => {
    return services
        .filter((service) => service.selected)
        .map((service) => {
            const { id, places } = service
            return { id, places }
        })
}

export const getMonthRange = (date: Date) => [
    startOfWeek(startOfMonth(date)),
    endOfWeek(endOfMonth(date)),
]

export const wbkBackendDate = (date: Date) =>
    wbkFormat(getUnixTime(date), 'YYYY-m-d')

export const wbkExtractBackendDate = (date: string) => {
    const [year, month, day] = date.split(',').map(Number)
    return new Date(year, month, day)
}

export const wbkFormatPrice = (price: number | string, placeholder: string) => {
    const priceNum = typeof price === 'string' ? parseFloat(price) : price
    const formattedPrice = priceNum.toFixed(2)
    return placeholder.replace('#price', formattedPrice)
}

export const constructFormData = (formObj: IBookingFormObj) => {
    const { fields, services, formData, userTimezone } = formObj

    const formPlaces: Record<number, IFormPlace[]> = {}
    services
        .filter((service) => service.selected)
        .forEach(({ id, places, quantity }) => {
            if (!places || places.length === 0) return

            formPlaces[id] = places.map(({ timeslot, date }) => ({
                date,
                time: timeslot,
                quantity,
            }))
        })

    const fieldValues = fields.reduce(
        (
            acc: Record<
                string,
                TAcceptedInputValues | Record<string, TAcceptedInputValues>
            >,
            field
        ) => {
            const { slug, value, type, placeholder, checkboxText } = field

            if (defautlFields.includes(slug)) {
                acc[slug] = value
            } else if (type === 'file') {
                acc['attachments'] = {
                    ...((acc['attachments'] || {}) as Record<
                        string,
                        TAcceptedInputValues
                    >),
                    [slug]: value,
                }
            } else {
                acc['extra'] = {
                    ...((acc['extra'] || {}) as Record<
                        string,
                        TAcceptedInputValues
                    >),
                    [slug]: wbkCreateExtraFieldValue(
                        slug,
                        type === 'checkbox' && checkboxText
                            ? checkboxText
                            : placeholder || '',
                        type === 'dropdown' && Array.isArray(value)
                            ? (value as IOption[])
                                  .map((item) => item.value)
                                  .join(',')
                            : type === 'dropdown' && !Array.isArray(value)
                              ? (value as IOption)?.value
                              : value
                    ),
                }
            }

            return acc
        },
        {}
    )

    return {
        ...formData,
        ...fieldValues,
        extra:
            (fieldValues?.extra &&
                JSON.stringify(
                    Object.values(fieldValues?.extra).map((fieldJSON) =>
                        JSON.parse(fieldJSON)
                    )
                )) ||
            '',
        places: formPlaces,
        services: services
            .filter(({ selected }) => selected)
            .flatMap(({ id, places }) =>
                places && places.length > 0
                    ? Array(places.length).fill(id)
                    : [id]
            ),
        offset: new Date().getTimezoneOffset(),
        time_zone_client: userTimezone,
        locale: document.documentElement.getAttribute('lang') || 'en-US',
    }
}

export const wbkCalculateAmount = ({
    quantity,
    price,
    places,
}: IServiceProps) =>
    Number(price) * Number(quantity) * Number(places?.length || 1)

export const wbkCreateExtraFieldValue = (
    slug: string,
    label: string,
    value: TAcceptedInputValues
) => {
    return JSON.stringify([slug, label, value])
}

export const extractFormValue = ({ fieldName, formData }: any) => {
    if (defautlFields.includes(fieldName)) return formData[fieldName]

    if (formData.extra === '') return null

    const extractedExtra = JSON.parse(formData.extra)

    if (!extractedExtra) return null

    const fieldVal = extractedExtra.find(
        (field: string[]) => field[0] === fieldName
    )

    if (!fieldVal) return null

    const [slug, label, value] = fieldVal

    return value
}
